var __classPrivateFieldSet = (this && this.__classPrivateFieldSet) || function (receiver, state, value, kind, f) {
    if (kind === "m") throw new TypeError("Private method is not writable");
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a setter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot write private member to an object whose class did not declare it");
    return (kind === "a" ? f.call(receiver, value) : f ? f.value = value : state.set(receiver, value)), value;
};
var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
};
var _TypedStorage_instances, _TypedStorage_namespace, _TypedStorage_logger, _TypedStorage_parsers, _TypedStorage_storage, _TypedStorage_window, _TypedStorage_namespacedKey, _TypedStorage_tryParse;
import globalLogger from "@lazarentertainment/js-logger";
import { useCallback, useEffect, useRef, useState } from "react";
import { throttleTrailing } from "./debounce";
export class TypedStorage {
    constructor({ namespace, logger, parsers, storage, window, }) {
        _TypedStorage_instances.add(this);
        _TypedStorage_namespace.set(this, void 0);
        _TypedStorage_logger.set(this, void 0);
        _TypedStorage_parsers.set(this, void 0);
        _TypedStorage_storage.set(this, void 0);
        _TypedStorage_window.set(this, void 0);
        _TypedStorage_namespacedKey.set(this, (key) => `${__classPrivateFieldGet(this, _TypedStorage_namespace, "f")}.${key}`);
        __classPrivateFieldSet(this, _TypedStorage_namespace, namespace, "f");
        __classPrivateFieldSet(this, _TypedStorage_logger, typeof logger === "function" ? logger("TypedJsonStorage") : logger, "f");
        __classPrivateFieldSet(this, _TypedStorage_parsers, parsers, "f");
        __classPrivateFieldSet(this, _TypedStorage_storage, storage, "f");
        __classPrivateFieldSet(this, _TypedStorage_window, window, "f");
    }
    getItem(key) {
        const data = __classPrivateFieldGet(this, _TypedStorage_storage, "f").getItem(__classPrivateFieldGet(this, _TypedStorage_namespacedKey, "f").call(this, key));
        return data === null ? null : __classPrivateFieldGet(this, _TypedStorage_instances, "m", _TypedStorage_tryParse).call(this, key, data);
    }
    setItem(key, data) {
        __classPrivateFieldGet(this, _TypedStorage_storage, "f").setItem(__classPrivateFieldGet(this, _TypedStorage_namespacedKey, "f").call(this, key), JSON.stringify(data));
    }
    removeItem(key) {
        __classPrivateFieldGet(this, _TypedStorage_storage, "f").removeItem(__classPrivateFieldGet(this, _TypedStorage_namespacedKey, "f").call(this, key));
    }
    subscribe(key, callback) {
        const listener = ({ key: eventKey, newValue }) => {
            if (eventKey === __classPrivateFieldGet(this, _TypedStorage_namespacedKey, "f").call(this, key)) {
                if (newValue === null) {
                    // This indicates storage was explicitly cleared, which is
                    // allowed. We should notify the callback in this case.
                    callback(null);
                }
                else {
                    // If data is null here, then it was in an invalid state. This
                    // could be due to user modifying their data, or it could be
                    // a bug elsewhere. Don't notify callback at this point. The
                    // tryParse method will delete the invalid data, triggering
                    // another "storage" event with newValue=null (which will trigger
                    // callback by the condition above this).
                    const data = __classPrivateFieldGet(this, _TypedStorage_instances, "m", _TypedStorage_tryParse).call(this, key, newValue);
                    if (data) {
                        callback(data);
                    }
                }
            }
        };
        __classPrivateFieldGet(this, _TypedStorage_window, "f").addEventListener("storage", listener);
        return () => {
            __classPrivateFieldGet(this, _TypedStorage_window, "f").removeEventListener("storage", listener);
        };
    }
}
_TypedStorage_namespace = new WeakMap(), _TypedStorage_logger = new WeakMap(), _TypedStorage_parsers = new WeakMap(), _TypedStorage_storage = new WeakMap(), _TypedStorage_window = new WeakMap(), _TypedStorage_namespacedKey = new WeakMap(), _TypedStorage_instances = new WeakSet(), _TypedStorage_tryParse = function _TypedStorage_tryParse(key, data) {
    const parse = __classPrivateFieldGet(this, _TypedStorage_parsers, "f")[key];
    try {
        return parse(JSON.parse(data));
    }
    catch (e) {
        __classPrivateFieldGet(this, _TypedStorage_logger, "f").error("parse failed", e);
        // The data is in an invalid state and should probably be removed.
        this.removeItem(key);
        return null;
    }
};
export function useLocalStorage(namespace, parsers, logger = globalLogger) {
    const storage = useRef(null);
    if (!storage.current && typeof window !== "undefined") {
        storage.current = new TypedStorage({
            namespace,
            logger,
            storage: window.localStorage,
            window,
            parsers,
        });
    }
    return storage.current;
}
export function useStorageState(storage, key, { defaultValue, throttleMs = 0 }) {
    const [value, setValue] = useState(() => { var _a, _b; return (_b = (_a = storage === null || storage === void 0 ? void 0 : storage.getItem(key)) !== null && _a !== void 0 ? _a : defaultValue) !== null && _b !== void 0 ? _b : null; });
    const updateStorage = useCallback(throttleTrailing((newValue) => {
        if (newValue) {
            storage === null || storage === void 0 ? void 0 : storage.setItem(key, newValue);
        }
        else {
            storage === null || storage === void 0 ? void 0 : storage.removeItem(key);
        }
    }, Math.max(throttleMs, 0)), [key, storage, throttleMs]);
    const updateValueAndStorage = useCallback((newValue) => {
        setValue(newValue);
        updateStorage(newValue);
    }, [updateStorage]);
    useEffect(() => storage === null || storage === void 0 ? void 0 : storage.subscribe(key, setValue), [storage]);
    return [value, updateValueAndStorage];
}
